import { formatDistance, formatDuration, formatSpeed } from './format';

export type HudState = {
  speed: number;
  maxSpeed: number;
  throttle: number;
  altitude: number;
  nearest: string;
  landed: boolean;
  status: string;
  pointerLocked: boolean;
  target: { name: string; distance: number; eta: number; autopilot: boolean; color: string } | null;
  stick: { x: number; y: number };
  shield: number;
  maxShield: number;
  score: number;
  respawnIn: number | null;
  damage: number; // 0..1 vignette
  logbook: { count: number; total: number; next: string };
  warp: { state: 'off' | 'spool' | 'on'; charge: number; blocked: string | null };
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent: HTMLElement, text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  if (text) e.textContent = text;
  parent.appendChild(e);
  return e;
}

/** Flight HUD: speed / throttle / altitude cluster, reticle, status line. */
export class Hud {
  readonly root: HTMLElement;
  private readonly speed: HTMLElement;
  private readonly speedMax: HTMLElement;
  private readonly throttleFill: HTMLElement;
  private readonly altitude: HTMLElement;
  private readonly altitudeLabel: HTMLElement;
  private readonly status: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly stickDot: HTMLElement;
  private readonly targetBox: HTMLElement;
  private readonly targetName: HTMLElement;
  private readonly targetInfo: HTMLElement;
  private readonly targetAp: HTMLElement;
  private readonly shieldFill: HTMLElement;
  private readonly shieldText: HTMLElement;
  private readonly score: HTMLElement;
  private readonly vignette: HTMLElement;
  private readonly deathOverlay: HTMLElement;
  private readonly deathCount: HTMLElement;
  private lastScore = 0;
  private readonly logCount: HTMLElement;
  private readonly logNext: HTMLElement;
  private readonly toastEl: HTMLElement;
  private readonly warpEl: HTMLElement;
  private readonly warpFill: HTMLElement;
  private readonly warpText: HTMLElement;
  private toastTimer = 0;

  constructor() {
    this.root = el('div', 'hud', document.getElementById('app')!);
    const reticle = el('div', 'reticle', this.root);
    el('div', 'reticle-ring', reticle);
    this.stickDot = el('div', 'reticle-dot', reticle);

    const flight = el('div', 'hud-flight', this.root);
    const speedBox = el('div', 'hud-block', flight);
    el('span', 'hud-label', speedBox, 'SPEED');
    this.speed = el('strong', 'hud-value', speedBox);
    this.speedMax = el('span', 'hud-sub', speedBox);
    const throttle = el('div', 'hud-throttle', flight);
    this.throttleFill = el('div', 'hud-throttle-fill', throttle);
    const altBox = el('div', 'hud-block', flight);
    this.altitudeLabel = el('span', 'hud-label', altBox, 'ALTITUDE');
    this.altitude = el('strong', 'hud-value', altBox);

    this.targetBox = el('div', 'hud-target', this.root);
    el('span', 'hud-label', this.targetBox, 'TARGET · T');
    this.targetName = el('strong', 'hud-target-name', this.targetBox);
    this.targetInfo = el('span', 'hud-sub', this.targetBox);
    this.targetAp = el('span', 'hud-ap', this.targetBox);

    const shieldBox = el('div', 'hud-shield', this.root);
    el('span', 'hud-label', shieldBox, 'SHIELD');
    const bar = el('div', 'hud-shield-bar', shieldBox);
    this.shieldFill = el('div', 'hud-shield-fill', bar);
    this.shieldText = el('span', 'hud-sub', shieldBox);

    const scoreBox = el('div', 'hud-score', this.root);
    el('span', 'hud-label', scoreBox, 'POINTS');
    this.score = el('strong', 'hud-value', scoreBox, '0');

    this.warpEl = el('div', 'hud-warp', flight);
    el('span', 'hud-label', this.warpEl, 'WARP · H');
    const wb = el('div', 'hud-warp-bar', this.warpEl);
    this.warpFill = el('div', 'hud-warp-fill', wb);
    this.warpText = el('span', 'hud-warp-text', this.warpEl);

    const logBox = el('div', 'hud-log', this.root);
    this.logCount = el('span', 'hud-label', logBox, 'LOGBOOK');
    this.logNext = el('span', 'hud-log-next', logBox);
    this.toastEl = el('div', 'hud-toast', this.root);

    this.vignette = el('div', 'hud-vignette', this.root);
    this.deathOverlay = el('div', 'hud-death hidden', this.root);
    el('div', 'hud-death-title', this.deathOverlay, 'SHIP DESTROYED');
    this.deathCount = el('div', 'hud-death-count', this.deathOverlay);

    this.status = el('div', 'hud-status', this.root);
    this.hint = el('div', 'hud-hint', this.root, 'Click to take control · W/S throttle · Mouse steer · A/D roll · R/F up/down · H warp · T target · J autopilot · M map · C camera');
  }

  update(s: HudState): void {
    this.speed.textContent = formatSpeed(s.speed);
    this.speedMax.textContent = `max ${formatSpeed(s.maxSpeed)}`;
    const t = Math.max(-0.15, Math.min(1, s.throttle));
    this.throttleFill.style.transform = `scaleY(${Math.abs(t)})`;
    this.throttleFill.classList.toggle('reverse', t < 0);
    this.altitudeLabel.textContent = `ALTITUDE · ${s.nearest.toUpperCase()}`;
    this.altitude.textContent = s.landed ? 'LANDED' : formatDistance(s.altitude);
    this.status.textContent = s.status;
    this.hint.classList.toggle('hidden', s.pointerLocked);
    if (s.target) {
      this.targetBox.classList.remove('hidden');
      this.targetName.textContent = s.target.name;
      this.targetName.style.color = s.target.color;
      this.targetInfo.textContent = `${formatDistance(s.target.distance)} · ETA ${formatDuration(s.target.eta)}`;
      this.targetAp.textContent = s.target.autopilot ? 'AUTOPILOT ENGAGED · J to cancel' : 'J · autopilot';
      this.targetAp.classList.toggle('on', s.target.autopilot);
    } else this.targetBox.classList.add('hidden');
    const sh = Math.max(0, s.shield / s.maxShield);
    this.shieldFill.style.transform = `scaleX(${sh})`;
    this.shieldFill.classList.toggle('low', sh < 0.3);
    this.shieldText.textContent = `${Math.ceil(s.shield)} / ${Math.round(s.maxShield)}`;
    if (s.score !== this.lastScore) {
      this.score.textContent = s.score.toLocaleString('en-US');
      if (s.score > this.lastScore) {
        this.score.classList.remove('pop');
        void this.score.offsetWidth;
        this.score.classList.add('pop');
      }
      this.lastScore = s.score;
    }
    const w = s.warp;
    this.warpEl.dataset.state = w.state === 'off' && w.blocked ? 'locked' : w.state;
    this.warpFill.style.transform = `scaleX(${w.state === 'on' ? 1 : w.state === 'spool' ? w.charge : 0})`;
    this.warpText.textContent = w.state === 'on' ? 'ACTIVE' : w.state === 'spool' ? `CHARGING ${Math.round(w.charge * 100)}%` : w.blocked ? 'MASS-LOCKED' : 'READY';
    this.logCount.textContent = `LOGBOOK ${s.logbook.count}/${s.logbook.total} · ESC`;
    this.logNext.textContent = `Next: ${s.logbook.next}`;
    this.vignette.style.opacity = String(Math.min(1, s.damage));
    this.deathOverlay.classList.toggle('hidden', s.respawnIn === null);
    if (s.respawnIn !== null) this.deathCount.textContent = `Respawning in ${Math.ceil(s.respawnIn)}`;
    this.stickDot.style.transform = `translate(${s.stick.x * 60}px, ${s.stick.y * 60}px)`;
  }

  toast(title: string, sub = ''): void {
    this.toastEl.innerHTML = `<b></b><span></span>`;
    (this.toastEl.firstChild as HTMLElement).textContent = title;
    (this.toastEl.lastChild as HTMLElement).textContent = sub;
    this.toastEl.classList.remove('show');
    void this.toastEl.offsetWidth;
    this.toastEl.classList.add('show');
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('show'), 4200);
  }

  /** In the bridge view the dashboard screens carry flight data, so hide the duplicates. */
  setCockpit(on: boolean): void {
    this.root.classList.toggle('cockpit', on);
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
  }
}
